import type {
  AuditLogEvent,
  AuditLogEventChangeOperation,
  GroupUpdateCoverageCategory,
  GroupUpdateRecipient,
  GroupUpdateReport,
  GroupUpdateResource
} from '../../../domain/audit-log-event'

import { CaretRightOutlined, CheckOutlined, CopyOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Button, Card, Checkbox, Col, Row, Typography } from 'antd'
import { AxiosError } from 'axios'
import cx from 'classnames'
import hljs from 'highlight.js/lib/core'
import { cloneDeep } from 'lodash'
import { useMemo, useState } from 'react'

import $api from '../../../API'
import { AuditLogAction } from '../../../domain/audit-log-action.enum'
import { AuditLogResourceType } from '../../../domain/audit-log-resource-type.enum'
import { label as reportLabel, mapper as reportMapper } from '../../../domain/cr-report-type.enum'
import { diffApply } from '../../../lib/just-diff-apply'
import * as constants from '../constants'
import * as utils from '../utils'

interface Props {
  auditLog: AuditLogEvent
}

export const GroupUpdateExpandableRow = (props: Props): JSX.Element | null => {
  const auditLogResource = props.auditLog.resource
  const auditLogResourceType = auditLogResource?.type
  const auditLogAction = props.auditLog.action
  const auditLogChanges = props.auditLog.changes ?? []

  if (
    auditLogResourceType !== AuditLogResourceType.Group ||
    auditLogAction !== AuditLogAction.Update
  ) {
    return null
  }

  const changesWithoutExludedPaths =
    auditLogChanges?.filter(
      diff => !constants.GROUP_UPDATE_EXCLUDED_PATHS.some(path => diff.path.includes(path))
    ) ?? []

  return (
    <div className='flex flex-col gap-4 p-6'>
      <Card
        title={
          <RowTitle
            groupName={auditLogResource?.name}
            groupNumber={auditLogResource?.number}
            locationName={auditLogResource?.location?.name}
          />
        }
        headStyle={{ borderBottom: changesWithoutExludedPaths.length === 0 ? 'none' : undefined }}
        bodyStyle={{ padding: 0 }}
      >
        <ChangesSection changes={auditLogChanges} resource={auditLogResource} />
      </Card>

      <RawJsonsSection resource={auditLogResource} changes={auditLogChanges} />
    </div>
  )
}

interface PayloadSectionProps {
  groupName: string | undefined
  groupNumber: string | undefined
  locationName: string | undefined
}

const RowTitle = (props: PayloadSectionProps): JSX.Element | null => {
  if (!props.groupName && !props.groupNumber && !props.locationName) {
    return null
  }

  const searchParams = new URLSearchParams([
    [...(props.groupNumber ? ['query', props.groupNumber.toLowerCase()] : [])]
  ])

  return (
    <div className='flex gap-2 items-center'>
      <Typography.Link
        className='text-lg'
        href={props.groupNumber ? `/groups/scheduler?${searchParams.toString()}` : undefined}
        target='_blank'
      >
        {props.groupNumber}{' '}
      </Typography.Link>
      <Typography.Text className='text-lg'>{props.groupName}</Typography.Text>
      <Typography.Text className='text-lg' type='secondary'>
        {props.locationName ?? 'N/A'}
      </Typography.Text>
    </div>
  )
}

interface ChangesSectionProps {
  changes: AuditLogEvent['changes']
  resource: AuditLogEvent['resource']
}

const ChangesSection = (props: ChangesSectionProps): JSX.Element | null => {
  const changesWithoutExludedPaths =
    props.changes?.filter(
      diff => !constants.GROUP_UPDATE_EXCLUDED_PATHS.some(path => diff.path.includes(path))
    ) ?? []

  if (changesWithoutExludedPaths.length === 0) {
    return null
  }

  const groupedChanges = utils.buildGroupUpdateChanges(changesWithoutExludedPaths)

  let currentResource: GroupUpdateResource | undefined

  try {
    currentResource = diffApply(
      cloneDeep(props.resource),
      props.changes ?? []
    ) as GroupUpdateResource
  } catch (error) {
    console.warn('Failed to apply changes:', error)
  }

  return (
    <div>
      {Object.entries(groupedChanges).map(([path, changes]) => {
        if (changes === undefined) {
          return null
        }

        if (changes.length === 1 && changes[0]?.path && changes[0].path.length === 1) {
          const operation = changes[0]?.op
          const path = changes[0]?.path[0] as string
          const prevValue = utils.getGroupUpdateValueByPath(props.resource, changes[0]?.path ?? [])
          const value = changes[0]?.value as string

          return (
            <DiffItem
              key={path}
              operation={operation}
              path={path}
              prevValue={prevValue}
              currentValue={value}
              prevResource={props.resource as GroupUpdateResource}
              currentResource={currentResource}
            />
          )
        }

        const prevValue = utils.getGroupUpdateValueByPath(props.resource, [path])
        const currentValue = utils.getGroupUpdateValueByPath(currentResource, [path])

        return (
          <DiffItem
            key={path}
            operation='replace'
            path={path}
            prevValue={prevValue}
            currentValue={currentValue}
            prevResource={props.resource as GroupUpdateResource}
            currentResource={currentResource}
          />
        )
      })}
    </div>
  )
}

interface ScheduleDiffProps {
  value: Record<string, number[]>
  type: 'previous' | 'new'
}

const ScheduleDiff = (props: ScheduleDiffProps): JSX.Element => {
  const days = useMemo(() => utils.generateWeek(), [])
  const weeks = useMemo(() => Array.from({ length: 5 }, (_, i) => i + 1), [])

  return (
    <div className='bg-white p-4 border rounded-md grid grid-cols-[10rem,_1fr] gap-1'>
      {days.map(day => {
        if (!day) {
          return null
        }

        const dayName = day.slice(0, 3).toUpperCase()
        const isAllWeekChosen = props.value?.[dayName] ?? []

        return (
          <div key={day} className='contents pointer-events-none'>
            <Checkbox
              disabled={isAllWeekChosen.length === 0}
              checked={isAllWeekChosen.length === weeks.length}
              indeterminate={isAllWeekChosen.length > 0 && isAllWeekChosen.length < weeks.length}
              className={cx({
                '[&_.ant-checkbox-inner]:bg-zinc-300 [&_.ant-checkbox-inner]:border-zinc-300':
                  props.type === 'previous' && isAllWeekChosen.length === weeks.length,
                '[&_.ant-checkbox-inner::after]:bg-zinc-300':
                  props.type === 'previous' &&
                  isAllWeekChosen.length > 0 &&
                  isAllWeekChosen.length < weeks.length
              })}
            >
              {day}
            </Checkbox>

            <Checkbox.Group value={isAllWeekChosen}>
              {weeks.map(week => {
                return (
                  <Checkbox
                    key={week}
                    value={week}
                    disabled={!isAllWeekChosen.includes(week)}
                    className={cx({
                      '[&_.ant-checkbox-inner]:bg-zinc-300  [&_.ant-checkbox-inner]:border-zinc-300':
                        props.type === 'previous' && isAllWeekChosen.includes(week)
                    })}
                  >
                    {week}
                  </Checkbox>
                )
              })}
            </Checkbox.Group>
          </div>
        )
      })}
    </div>
  )
}

interface ResourceAndChangesSectionProps {
  resource: AuditLogEvent['resource']
  changes: AuditLogEvent['changes']
}

const RawJsonsSection = (props: ResourceAndChangesSectionProps): JSX.Element => {
  const [isJsonsSectionExpanded, setIsJsonsSectionExpanded] = useState(false)

  const [copiedMap, setCopiedMap] = useState({
    resource: false,
    changes: false
  })

  const highlightedResource = hljs.highlight(JSON.stringify(props.resource, null, 2), {
    language: 'json'
  }).value

  const highlightedChanges = hljs.highlight(JSON.stringify(props.changes, null, 2), {
    language: 'json'
  }).value

  const copyJson = async (type: 'resource' | 'changes', data: unknown): Promise<void> => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2))
      setCopiedMap(state => ({ ...state, [type]: true }))
      setTimeout(() => {
        setCopiedMap({ resource: false, changes: false })
      }, 2000)
    } catch (error) {
      console.error('Copy json failed:', error)
    }
  }

  const handleCopyJson = (type: 'resource' | 'changes', data: unknown) => (): void => {
    void copyJson(type, data)
  }

  const handleJsonClick = (): void => {
    setIsJsonsSectionExpanded(prev => !prev)
  }

  return (
    <div className='rounded-xl border border-[#F0F0F0]'>
      <div onClick={handleJsonClick}>
        <div className='flex gap-2 items-center p-4 cursor-pointer'>
          <CaretRightOutlined
            className={cx('text-[#6E727D]', {
              'rotate-90': isJsonsSectionExpanded
            })}
          />

          <Typography.Title level={5} className='!mb-0'>
            JSON
          </Typography.Title>
        </div>
      </div>

      {isJsonsSectionExpanded && (
        <div className='border-t'>
          <div className='p-4'>
            <div className='grid grid-cols-2 gap-4'>
              <div className='relative'>
                <Typography.Title level={5}>Resource</Typography.Title>

                <pre
                  className='overflow-x-auto overflow-y-scroll p-2 max-h-96 text-xs bg-white rounded border'
                  dangerouslySetInnerHTML={{ __html: highlightedResource }}
                />

                <Button
                  icon={copiedMap.resource ? <CheckOutlined /> : <CopyOutlined />}
                  onClick={handleCopyJson('resource', props.resource)}
                  className='flex absolute right-8 top-12 justify-center items-center'
                  size='small'
                >
                  {copiedMap.resource ? 'Copied' : 'Copy'}
                </Button>
              </div>

              <div className='relative'>
                <Typography.Title level={5}>Changes</Typography.Title>

                <pre
                  className='overflow-x-auto overflow-y-scroll p-2 max-h-96 text-xs bg-white rounded border'
                  dangerouslySetInnerHTML={{ __html: highlightedChanges }}
                />

                <Button
                  icon={copiedMap.changes ? <CheckOutlined /> : <CopyOutlined />}
                  onClick={handleCopyJson('changes', props.changes)}
                  className='flex absolute right-8 top-12 justify-center items-center'
                  size='small'
                >
                  {copiedMap.changes ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

interface DiffItemProps {
  operation: AuditLogEventChangeOperation
  path: string
  prevValue: unknown
  currentValue: unknown
  prevResource: GroupUpdateResource
  currentResource: GroupUpdateResource | undefined
}

const DiffItem = (props: DiffItemProps): JSX.Element | null => {
  const operation = props.operation

  if (operation === undefined) {
    return null
  }

  const title = utils.renderGroupUpdateHumanFieldName(props.path)

  if (props.path === 'schedule') {
    const prevSchedule = props.prevValue as Record<string, number[]>
    const currentSchedule = props.currentValue as Record<string, number[]>

    const isPrevScheduleEmpty = utils.isEmptySchedule(prevSchedule)
    const isCurrentScheduleEmpty = utils.isEmptySchedule(currentSchedule)

    const renderHumanReadableSchedule = (humanReadableScheduleItem: string) => (
      <span key={humanReadableScheduleItem}>{humanReadableScheduleItem}</span>
    )

    return (
      <Row className='px-8 py-5 border-b last:border-b-0'>
        <Col span={4} className='flex items-start'>
          <Typography.Title level={5} className='!mb-0 py-2 pr-4'>
            Schedule
          </Typography.Title>
        </Col>

        <Col>
          <div className='flex flex-col gap-4'>
            <div className='flex items-center w-full h-full leading-7'>
              {!isPrevScheduleEmpty &&
                !isCurrentScheduleEmpty &&
                typeof prevSchedule === 'object' &&
                prevSchedule !== null && (
                  <div className='flex flex-1 gap-4 items-start w-full'>
                    <div className='flex flex-[1.15] gap-4 items-center'>
                      <div className='flex flex-1 flex-col px-4 py-2 bg-[#FFFBE6] text-[#956F00] rounded'>
                        {utils.humanReadableSchedule(prevSchedule).map(renderHumanReadableSchedule)}
                      </div>

                      <span>→</span>
                    </div>

                    <div className='flex flex-1 flex-col px-4 py-2 bg-[#FFFBE6] rounded text-[#956F00]'>
                      {utils
                        .humanReadableSchedule(currentSchedule)
                        .map(renderHumanReadableSchedule)}
                    </div>
                  </div>
                )}

              {isPrevScheduleEmpty && !isCurrentScheduleEmpty && (
                <div className='flex flex-1 flex-col px-4 py-2 bg-[#DCFFDD] rounded text-[#2E7E0F]'>
                  {utils.humanReadableSchedule(currentSchedule).map(renderHumanReadableSchedule)}
                </div>
              )}

              {!isPrevScheduleEmpty &&
                isCurrentScheduleEmpty &&
                typeof prevSchedule === 'object' &&
                prevSchedule !== null && (
                  <div className='flex flex-1 flex-col px-4 py-2 bg-[#FFF2F0] rounded text-[#FF4D4F] line-through'>
                    {utils.humanReadableSchedule(prevSchedule).map(renderHumanReadableSchedule)}
                  </div>
                )}
            </div>

            <div className='flex gap-4'>
              <div className='flex-1'>
                <ScheduleDiff value={prevSchedule ?? {}} type='previous' />
              </div>

              <span className='self-center'>→</span>

              <div className='flex-1'>
                <ScheduleDiff value={currentSchedule} type='new' />
              </div>
            </div>
          </div>
        </Col>
      </Row>
    )
  }

  if (props.path === 'notification_recipients') {
    const prevRecipients = (props.prevValue as GroupUpdateRecipient[]) ?? []
    const currentRecipients = (props.currentValue as GroupUpdateRecipient[]) ?? []

    const allReports = [
      ...(props.prevResource?.reports ?? []),
      ...(props.currentResource?.reports ?? [])
    ]

    const uniqueReports = allReports.filter(
      (report, index, self) => index === self.findIndex(r => r.id === report.id)
    )

    if (['remove', 'add'].includes(props.operation)) {
      return (
        <div className='border-b last:border-b-0'>
          <Row className='px-8 py-5'>
            <Col span={4} className='flex items-start'>
              <Typography.Title level={5} className='!mb-0 py-2 pr-4'>
                {title}
              </Typography.Title>
            </Col>

            <Col>
              <div className='flex flex-col flex-1 gap-2'>
                {props.operation === 'remove' &&
                  prevRecipients.map(recipient => {
                    return (
                      <RecipientDiff
                        key={recipient?.id}
                        operation={operation}
                        recipient={recipient}
                        reports={uniqueReports}
                      />
                    )
                  })}

                {props.operation === 'add' &&
                  currentRecipients.map(recipient => {
                    return (
                      <RecipientDiff
                        key={recipient?.id}
                        operation={operation}
                        recipient={recipient}
                        reports={uniqueReports}
                      />
                    )
                  })}
              </div>
            </Col>
          </Row>
        </div>
      )
    }

    if (props.operation === 'replace') {
      const removedRecipients = prevRecipients.filter(
        prevRecipient =>
          !currentRecipients.some(currentRecipient => currentRecipient.id === prevRecipient.id)
      )

      const addedRecipients = currentRecipients.filter(
        currentRecipient =>
          !prevRecipients.some(prevRecipient => prevRecipient.id === currentRecipient.id)
      )

      const prevChangedRecipients = prevRecipients.filter(recipient => {
        const changedRecipient = currentRecipients.find(r => r.id === recipient.id)

        if (changedRecipient === undefined) {
          return false
        }

        return (
          changedRecipient.email !== recipient.email ||
          (changedRecipient.attachments ?? []).join(',') !==
            (recipient.attachments ?? []).join(',') ||
          changedRecipient.delayed_notice !== recipient.delayed_notice
        )
      })

      const currentChangedRecipients = currentRecipients.filter(recipient => {
        const changedRecipient = prevRecipients.find(r => r.id === recipient.id)

        if (changedRecipient === undefined) {
          return false
        }

        return (
          changedRecipient.email !== recipient.email ||
          (changedRecipient.attachments ?? []).join(',') !==
            (recipient.attachments ?? []).join(',') ||
          changedRecipient.delayed_notice !== recipient.delayed_notice
        )
      })

      const allReports = [
        ...(props.prevResource?.reports ?? []),
        ...(props.currentResource?.reports ?? [])
      ]

      const uniqueReports = allReports.filter(
        (report, index, self) => index === self.findIndex(r => r.id === report.id)
      )

      return (
        <>
          {prevChangedRecipients.length > 0 && currentChangedRecipients.length > 0 && (
            <div className='border-b last:border-b-0'>
              <Row className='px-8 py-5'>
                <Col span={4} className='flex items-start'>
                  <Typography.Title level={5} className='!mb-0 py-2 pr-4'>
                    {title}
                  </Typography.Title>
                </Col>

                <Col>
                  <div className='flex gap-2'>
                    <div className='flex flex-col flex-1 gap-2'>
                      {prevChangedRecipients.map(recipient => {
                        return (
                          <RecipientDiff
                            key={recipient?.id}
                            operation='replace'
                            recipient={recipient}
                            reports={uniqueReports}
                          />
                        )
                      })}
                    </div>

                    <span className='self-center'>→</span>

                    <div className='flex flex-col flex-1 gap-2'>
                      {currentChangedRecipients.map(recipient => {
                        return (
                          <RecipientDiff
                            key={recipient?.id}
                            operation='replace'
                            recipient={recipient}
                            reports={uniqueReports}
                          />
                        )
                      })}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {removedRecipients.length > 0 && (
            <div className='border-b last:border-b-0'>
              <Row className='px-8 py-5'>
                <Col span={4} className='flex items-start'>
                  <Typography.Title level={5} className='!mb-0 py-2 pr-4'>
                    {title}
                  </Typography.Title>
                </Col>

                <Col>
                  <div className='flex flex-col flex-1 gap-2'>
                    {removedRecipients.map(recipient => {
                      return (
                        <RecipientDiff
                          key={recipient?.id}
                          operation='remove'
                          recipient={recipient}
                          reports={uniqueReports}
                        />
                      )
                    })}
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {addedRecipients.length > 0 && (
            <div className='border-b last:border-b-0'>
              <Row className='px-8 py-5'>
                <Col span={4} className='flex items-start'>
                  <Typography.Title level={5} className='!mb-0 py-2 pr-4'>
                    {title}
                  </Typography.Title>
                </Col>

                <Col>
                  <div className='flex flex-col flex-1 gap-2'>
                    {addedRecipients.map(recipient => {
                      return (
                        <RecipientDiff
                          key={recipient?.id}
                          operation='add'
                          recipient={recipient}
                          reports={uniqueReports}
                        />
                      )
                    })}
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </>
      )
    }
  }

  if (props.path === 'reports') {
    const prevReports = props.prevValue as GroupUpdateReport[]
    const currentReports = props.currentValue as GroupUpdateReport[]

    if (['remove', 'add'].includes(props.operation)) {
      return (
        <div className='border-b last:border-b-0'>
          <Row className='px-8 py-5'>
            <Col span={4} className='flex items-start'>
              <Typography.Title level={5} className='!mb-0 py-2 pr-4'>
                {title}
              </Typography.Title>
            </Col>

            <Col>
              <div className='flex flex-col flex-1 gap-2'>
                {props.operation === 'remove' &&
                  prevReports.map(report => {
                    return <ReportDiff key={report?.id} operation={operation} report={report} />
                  })}

                {props.operation === 'add' &&
                  currentReports.map(report => {
                    return <ReportDiff key={report?.id} operation={operation} report={report} />
                  })}
              </div>
            </Col>
          </Row>
        </div>
      )
    }

    if (props.operation === 'replace') {
      const removedReports = prevReports.filter(
        prevReport => !currentReports.some(currentReport => currentReport.id === prevReport.id)
      )

      const addedReports = currentReports.filter(
        currentReport => !prevReports.some(prevReport => prevReport.id === currentReport.id)
      )

      const prevChangedReports = prevReports.filter(report => {
        const changedReport = currentReports.find(r => r.id === report.id)

        if (changedReport === undefined) {
          return false
        }

        return (
          changedReport.type !== report.type ||
          changedReport.options?.includePHI !== report.options?.includePHI ||
          changedReport.options?.includeDiagnosisDetails !== report.options?.includeDiagnosisDetails
        )
      })

      const currentChangedReports = currentReports.filter(report => {
        const changedReport = prevReports.find(r => r.id === report.id)

        if (changedReport === undefined) {
          return false
        }

        return (
          changedReport.type !== report.type ||
          changedReport.options?.includePHI !== report.options?.includePHI ||
          changedReport.options?.includeDiagnosisDetails !== report.options?.includeDiagnosisDetails
        )
      })

      return (
        <>
          {prevChangedReports.length > 0 && currentChangedReports.length > 0 && (
            <div className='border-b last:border-b-0'>
              <Row className='px-8 py-5'>
                <Col span={4} className='flex items-start'>
                  <Typography.Title level={5} className='!mb-0 py-2 pr-4'>
                    {title}
                  </Typography.Title>
                </Col>

                <Col>
                  <div className='flex gap-2'>
                    <div className='flex flex-col flex-1 gap-2'>
                      {prevChangedReports.map(report => {
                        return <ReportDiff key={report?.id} operation='replace' report={report} />
                      })}
                    </div>

                    <span className='self-center'>→</span>

                    <div className='flex flex-col flex-1 gap-2'>
                      {currentChangedReports.map(report => {
                        return <ReportDiff key={report?.id} operation='replace' report={report} />
                      })}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {removedReports.length > 0 && (
            <div className='border-b last:border-b-0'>
              <Row className='px-8 py-5'>
                <Col span={4} className='flex items-start'>
                  <Typography.Title level={5} className='!mb-0 py-2 pr-4'>
                    {title}
                  </Typography.Title>
                </Col>

                <Col>
                  <div className='flex flex-col flex-1 gap-2'>
                    {removedReports.map(report => {
                      return <ReportDiff key={report?.id} operation='remove' report={report} />
                    })}
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {addedReports.length > 0 && (
            <div className='border-b last:border-b-0'>
              <Row className='px-8 py-5'>
                <Col span={4} className='flex items-start'>
                  <Typography.Title level={5} className='!mb-0 py-2 pr-4'>
                    {title}
                  </Typography.Title>
                </Col>

                <Col>
                  <div className='flex flex-col flex-1 gap-2'>
                    {addedReports.map(report => {
                      return <ReportDiff key={report?.id} operation='add' report={report} />
                    })}
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </>
      )
    }
  }

  if (props.path === 'coverage_categories') {
    const prevCoverageCategories = props.prevValue as GroupUpdateCoverageCategory[]
    const currentCoverageCategories = props.currentValue as GroupUpdateCoverageCategory[]

    if (['remove', 'add'].includes(props.operation)) {
      return (
        <div className='border-b last:border-b-0'>
          <Row className='px-8 py-5'>
            <Col span={4} className='flex items-start'>
              <Typography.Title level={5} className='!mb-0 py-2 pr-4'>
                {title}
              </Typography.Title>
            </Col>

            <Col>
              <div className='flex flex-col flex-1 gap-2'>
                {props.operation === 'remove' &&
                  prevCoverageCategories.map(coverageCategory => {
                    return (
                      <CoverageCategoryDiff
                        key={coverageCategory?.id}
                        operation={operation}
                        coverageCategory={coverageCategory}
                      />
                    )
                  })}

                {props.operation === 'add' &&
                  currentCoverageCategories.map(coverageCategory => {
                    return (
                      <CoverageCategoryDiff
                        key={coverageCategory?.id}
                        operation={operation}
                        coverageCategory={coverageCategory}
                      />
                    )
                  })}
              </div>
            </Col>
          </Row>
        </div>
      )
    }

    if (props.operation === 'replace') {
      const removedCoverageCategories = prevCoverageCategories.filter(
        prevCoverageCategory =>
          !currentCoverageCategories.some(
            currentCoverageCategory => currentCoverageCategory.id === prevCoverageCategory.id
          )
      )

      const addedCoverageCategories = currentCoverageCategories.filter(
        currentCoverageCategory =>
          !prevCoverageCategories.some(
            prevCoverageCategory => prevCoverageCategory.id === currentCoverageCategory.id
          )
      )

      const prevChangedCoverageCategories = prevCoverageCategories.filter(coverageCategory => {
        const changedCoverageCategory = currentCoverageCategories.find(
          r => r.id === coverageCategory.id
        )

        if (changedCoverageCategory === undefined) {
          return false
        }

        return changedCoverageCategory.is_active !== coverageCategory.is_active
      })

      const currentChangedCoverageCategories = currentCoverageCategories.filter(
        coverageCategory => {
          const changedCoverageCategory = prevCoverageCategories.find(
            r => r.id === coverageCategory.id
          )

          if (changedCoverageCategory === undefined) {
            return false
          }

          return changedCoverageCategory.is_active !== coverageCategory.is_active
        }
      )

      return (
        <>
          {prevChangedCoverageCategories.length > 0 &&
            currentChangedCoverageCategories.length > 0 && (
              <div className='border-b last:border-b-0'>
                <Row className='px-8 py-5'>
                  <Col span={4} className='flex items-start'>
                    <Typography.Title level={5} className='!mb-0 py-2 pr-4'>
                      {title}
                    </Typography.Title>
                  </Col>

                  <Col>
                    <div className='flex gap-2'>
                      <div className='flex flex-col flex-1 gap-2'>
                        {prevChangedCoverageCategories.map(coverageCategory => {
                          return (
                            <CoverageCategoryDiff
                              key={coverageCategory?.id}
                              operation='replace'
                              coverageCategory={coverageCategory}
                            />
                          )
                        })}
                      </div>

                      <span className='self-center'>→</span>

                      <div className='flex flex-col flex-1 gap-2'>
                        {currentChangedCoverageCategories.map(coverageCategory => {
                          return (
                            <CoverageCategoryDiff
                              key={coverageCategory?.id}
                              operation='replace'
                              coverageCategory={coverageCategory}
                            />
                          )
                        })}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            )}

          {removedCoverageCategories.length > 0 && (
            <div className='border-b last:border-b-0'>
              <Row className='px-8 py-5'>
                <Col span={4} className='flex items-start'>
                  <Typography.Title level={5} className='!mb-0 py-2 pr-4'>
                    {title}
                  </Typography.Title>
                </Col>

                <Col>
                  <div className='flex flex-col flex-1 gap-2'>
                    {removedCoverageCategories.map(coverageCategory => {
                      return (
                        <CoverageCategoryDiff
                          key={coverageCategory?.id}
                          operation='remove'
                          coverageCategory={coverageCategory}
                        />
                      )
                    })}
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {addedCoverageCategories.length > 0 && (
            <div className='border-b last:border-b-0'>
              <Row className='px-8 py-5'>
                <Col span={4} className='flex items-start'>
                  <Typography.Title level={5} className='!mb-0 py-2 pr-4'>
                    {title}
                  </Typography.Title>
                </Col>

                <Col>
                  <div className='flex flex-col flex-1 gap-2'>
                    {addedCoverageCategories.map(coverageCategory => {
                      return (
                        <CoverageCategoryDiff
                          key={coverageCategory?.id}
                          operation='add'
                          coverageCategory={coverageCategory}
                        />
                      )
                    })}
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </>
      )
    }
  }

  return (
    <div className='border-b last:border-b-0'>
      <Row className='px-8 py-5'>
        <Col span={4} className='flex items-center'>
          <Typography.Title level={5} className='!mb-0 pr-4'>
            {title}
          </Typography.Title>
        </Col>

        <Col>
          <div className='flex justify-center items-center w-full h-full'>
            {props.operation === 'add' && (
              <span className='px-4 py-2 text-[#2E7E0F] bg-[#DCFFDD] rounded'>
                {utils.renderPrimitiveValue(props.currentValue)}
              </span>
            )}

            {props.operation === 'remove' && (
              <span className='px-4 py-2 bg-[#FFF2F0] text-[#FF4D4F] rounded line-through'>
                {utils.renderPrimitiveValue(props.prevValue)}
              </span>
            )}

            {props.operation === 'replace' && (
              <div className='flex gap-2 items-center'>
                <span className='px-2 py-1 text-[#956F00] bg-[#FFFBE6] rounded leading-7'>
                  {utils.renderPrimitiveValue(props.prevValue)}
                </span>
                <span>→</span>
                <span className='px-2 py-1 text-[#956F00] bg-[#FFFBE6] rounded leading-7'>
                  {utils.renderPrimitiveValue(props.currentValue)}
                </span>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </div>
  )
}

interface RecipientDiffProps {
  operation: AuditLogEventChangeOperation
  recipient: Partial<GroupUpdateRecipient>
  reports: Array<Partial<GroupUpdateReport>>
}

const RecipientDiff = (props: RecipientDiffProps): JSX.Element | null => {
  if (
    props.recipient?.email === undefined &&
    props.recipient?.attachments === undefined &&
    props.recipient?.delayed_notice === undefined
  ) {
    return null
  }

  const attachmentNames = (props.recipient?.attachments ?? []).map(attachmentId => {
    const report = props.reports.find(report => report.id === attachmentId)

    if (!report) {
      return attachmentId
    }

    const reportType = report.type

    if (reportType === undefined) {
      return attachmentId
    }

    return reportLabel(reportType, report.options)
  })

  return (
    <div
      className={cx('px-4 py-2 rounded', {
        'bg-[#DCFFDD] text-[#2E7E0F]': props.operation === 'add',
        'bg-[#FFF2F0] text-[#FF4D4F] line-through': props.operation === 'remove',
        'bg-[#FFFBE6] text-[#956F00]': props.operation === 'replace'
      })}
    >
      {props.recipient?.is_network !== undefined && (
        <div>
          <span className='font-medium'>Type: </span>
          <span>{props.recipient.is_network ? 'Network' : 'Group'}</span>
        </div>
      )}

      {props.recipient?.email && (
        <div className='flex gap-2 items-center'>
          <span className='font-medium'>Email: </span>
          <span>{props.recipient?.email}</span>
        </div>
      )}

      {attachmentNames.length > 0 && (
        <div>
          <span className='font-medium'>Attachments: </span>
          <span>{attachmentNames.join(', ')}</span>
        </div>
      )}

      <div>
        <span className='font-medium'>Delayed Notice: </span>
        <span>{props.recipient?.delayed_notice ? 'Enabled' : 'Disabled'}</span>
      </div>
    </div>
  )
}

interface ReportDiffProps {
  operation: AuditLogEventChangeOperation
  report: Partial<GroupUpdateReport>
}

const ReportDiff = (props: ReportDiffProps): JSX.Element | null => {
  if (
    !props.report?.type &&
    !props.report?.options?.includePHI &&
    !props.report?.options?.includeDiagnosisDetails
  ) {
    return null
  }

  return (
    <div
      className={cx('px-4 py-2 rounded', {
        'bg-[#DCFFDD] text-[#2E7E0F]': props.operation === 'add',
        'bg-[#FFF2F0] text-[#FF4D4F] line-through': props.operation === 'remove',
        'bg-[#FFFBE6] text-[#956F00]': props.operation === 'replace'
      })}
    >
      {props.report?.type && (
        <div className='flex gap-2 items-center'>
          <span className='font-medium'>Type: </span>
          <span>{reportMapper[props.report?.type]}</span>
        </div>
      )}

      <div>
        <span className='font-medium'>Include PHI: </span>
        <span>{props.report?.options?.includePHI ? 'Enabled' : 'Disabled'}</span>
      </div>

      <div>
        <span className='font-medium'>Include Diagnosis Details: </span>
        <span>{props.report?.options?.includeDiagnosisDetails ? 'Enabled' : 'Disabled'}</span>
      </div>
    </div>
  )
}

interface CoverageCategoryDiffProps {
  operation: AuditLogEventChangeOperation
  coverageCategory: Partial<GroupUpdateCoverageCategory>
}

const CoverageCategoryDiff = (props: CoverageCategoryDiffProps): JSX.Element | null => {
  const coverageCategoriesQuery = useQuery<unknown, unknown, Array<{ id: string; name: string }>>({
    queryKey: ['coverage-categories'],

    async queryFn() {
      try {
        const response = await $api.get('/coverage-categories')

        return response.data
      } catch (e) {
        if (e instanceof AxiosError) {
          throw new TypeError(`Request failed with status ${e.status} (${e.message})`)
        }
      }
    }
  })

  if (!props.coverageCategory?.coverage_category_id) {
    return null
  }

  const coverageCategory = coverageCategoriesQuery.data?.find(
    category => category.id === props.coverageCategory.coverage_category_id
  )

  if (!coverageCategory) {
    return null
  }

  return (
    <div
      className={cx('px-4 py-2 rounded', {
        'bg-[#DCFFDD] text-[#2E7E0F]': props.operation === 'add',
        'bg-[#FFF2F0] text-[#FF4D4F] line-through': props.operation === 'remove',
        'bg-[#FFFBE6] text-[#956F00]': props.operation === 'replace'
      })}
    >
      {coverageCategory.name && (
        <div className='flex gap-2 items-center'>
          <span className='font-medium'>{coverageCategory.name}: </span>
          <span>{props.coverageCategory.is_active ? 'Enabled' : 'Disabled'}</span>
        </div>
      )}
    </div>
  )
}
